import React, { useEffect, createContext, useContext } from 'react';

// Define theme colors - Light theme inspired by Hinge
export const themeColors = {
  primary: '#FF4458',          // Hinge's signature coral/pink
  primaryLight: '#FF677A',     // Lighter coral
  primaryDark: '#E73C4E',      // Darker coral
  secondary: '#4A90E2',        // Clean blue
  accent: '#FF4458',           // Matching coral for actions
  success: '#34C759',          // iOS green
  warning: '#FF9500',          // iOS orange
  error: '#FF3B30',            // iOS red
  info: '#007AFF',             // iOS blue
  
  background: '#FFFFFF',       // Pure white background
  backgroundSoft: '#FAFAFA',   // Very light grey
  surface: '#FFFFFF',          // Card backgrounds (white)
  surfaceVariant: '#F5F5F5',   // Light grey for subtle sections
  surfaceElevated: '#FFFFFF',  // Modals, elevated content
  
  text: '#1C1C1E',             // Dark grey text (iOS style)
  textPrimary: '#1C1C1E',      // Main text color
  textSecondary: '#8E8E93',    // Medium grey text
  textTertiary: '#C7C7CC',     // Light grey text
  textMuted: '#D1D1D6',        // Very light grey text
  
  border: '#E5E5EA',           // Light borders
  borderLight: '#F2F2F7',      // Very light borders
  
  shadow: '0 2px 16px rgba(0,0,0,0.1)',
  shadowLight: '0 1px 8px rgba(0,0,0,0.05)',
  shadowHeavy: '0 8px 32px rgba(0,0,0,0.15)',
};

// Define theme context type
type ThemeContextType = {
  colors: typeof themeColors;
};

// Create context with default values
const ThemeContext = createContext<ThemeContextType>({
  colors: themeColors,
});

// Custom hook to use the theme context
export const useTheme = () => useContext(ThemeContext);

// Provider component
export const ThemeProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  // Update document styles based on theme
  useEffect(() => {
    // Update root element with theme colors for CSS variables
    const root = document.documentElement;
    
    // Set background color
    document.body.style.backgroundColor = themeColors.background;
    document.body.style.color = themeColors.text;
    
    // Add light theme class
    document.documentElement.classList.add('light-theme');
    
    // Update meta theme-color for mobile browsers
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute('content', themeColors.background);
    }
  }, []);

  const value = {
    colors: themeColors,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};